<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-title">设备配置</div>
      <div class="section-actions">
        <button class="add-btn" type="button" @click="openDeviceModal">+ 新增设备</button>
      </div>
    </div>

    <div v-if="listLoading" class="list-loading">加载中...</div>
    <div v-else class="form-group device-list-section">
      <table class="edit-device-table">
        <thead>
          <tr>
            <th>设备名称</th>
            <th class="edit-device-action-col">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!devices.length">
            <td colspan="2" class="empty-cell">暂无设备</td>
          </tr>
          <tr v-for="device in devices" :key="device.deviceId">
            <td>
              <div
                v-if="editingDeviceId === device.deviceId"
                class="device-name-edit"
              >
                <input
                  v-model.trim="deviceEditDraft"
                  class="form-input device-name-input"
                  @keyup.enter="confirmEditDevice(device)"
                />
              </div>
              <span v-else>{{ device.deviceName }}</span>
            </td>
            <td class="edit-device-action-col">
              <template v-if="editingDeviceId === device.deviceId">
                <button
                  type="button"
                  class="mini-btn mini-btn-confirm"
                  :disabled="savingDeviceEditId === device.deviceId"
                  @click="confirmEditDevice(device)"
                >
                  {{ savingDeviceEditId === device.deviceId ? "提交中..." : "确认" }}
                </button>
                <button
                  type="button"
                  class="mini-btn mini-btn-cancel"
                  @click="cancelEditDevice"
                >
                  取消
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="mini-btn mini-btn-edit"
                  @click="startEditDevice(device)"
                >
                  修改
                </button>
                <button
                  type="button"
                  class="mini-btn mini-btn-delete"
                  @click="deleteEditDevice(device)"
                >
                  删除
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 新增设备弹框 -->
    <div
      v-if="showDeviceModal"
      class="modal-overlay"
      @click.self="closeDeviceModal"
    >
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">新增设备</span>
          <span class="modal-close" @click="closeDeviceModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">设备名称</label>
            <input
              v-model.trim="deviceForm.deviceName"
              class="form-input"
            />
          </div>
          <button
            class="modal-save-btn"
            type="button"
            :disabled="savingDevice"
            @click="saveDevice"
          >
            {{ savingDevice ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  addDevices,
  deleteDevice,
  listAllDevices,
  listProjectsWithDevices,
  updateDevice,
} from "@/api/device";

const SUCCESS_CODE = 20000;

export default {
  name: "ChairSettingPage",
  data() {
    return {
      listLoading: false,
      savingDevice: false,
      showDeviceModal: false,
      deviceForm: {
        deviceName: "",
      },
      editingDeviceId: null,
      savingDeviceEditId: null,
      deviceEditDraft: "",
      devices: [],
    };
  },
  mounted() {
    this.fetchDeviceList();
  },
  methods: {
    showMessage(type, text) {
      this.$message({
        message: text,
        type,
      });
    },
    openDeviceModal() {
      this.deviceForm = { deviceName: "" };
      this.showDeviceModal = true;
    },
    closeDeviceModal() {
      this.showDeviceModal = false;
    },
    isBizError(res) {
      return res && res.code !== undefined && res.code !== SUCCESS_CODE;
    },
    normalizeDevice(device) {
      if (!device) return null;
      if (typeof device === "string") {
        return { deviceId: device, deviceName: device };
      }
      return {
        deviceId: String(device.deviceId || device.id || ""),
        deviceName: device.deviceName || device.name || "",
      };
    },
    normalizeDeviceList(list) {
      if (!Array.isArray(list)) return [];
      return list.map((d) => this.normalizeDevice(d)).filter(Boolean);
    },
    mapDeviceList(list) {
      return this.normalizeDeviceList(list);
    },
    flattenDevicesFromProjects(projects) {
      const map = new Map();
      (projects || []).forEach((item) => {
        this.normalizeDeviceList(item.packages).forEach((d) => {
          if (d.deviceId && !map.has(d.deviceId)) {
            map.set(d.deviceId, d);
          }
        });
      });
      return Array.from(map.values());
    },
    mergeCreatedDevice(res) {
      const created = this.normalizeDevice(res && res.data);
      if (!created || !created.deviceId) return;
      const exists = this.devices.some((d) => d.deviceId === created.deviceId);
      if (!exists) {
        this.devices = [...this.devices, created];
      }
    },
    async fetchDeviceList() {
      this.listLoading = true;
      try {
        let devices = null;

        try {
          const res = await listAllDevices();
          if (!this.isBizError(res)) {
            devices = this.mapDeviceList(res && res.data);
          }
        } catch (e) {
          // listAllDevices 不可用时降级到项目接口
        }

        if (devices === null) {
          const res = await listProjectsWithDevices();
          if (this.isBizError(res)) {
            this.showMessage("error", res.msg || "列表加载失败");
            this.devices = [];
            return;
          }
          devices = this.flattenDevicesFromProjects(res && res.data);
        }

        this.devices = devices;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.devices = [];
      } finally {
        this.listLoading = false;
      }
    },
    async saveDevice() {
      if (!this.deviceForm.deviceName) {
        this.showMessage("warning", "请输入设备名称");
        return;
      }

      this.savingDevice = true;
      try {
        const res = await addDevices({
          deviceName: this.deviceForm.deviceName,
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "设备新增失败");
          return;
        }

        this.showMessage("success", (res && res.msg) || "设备新增成功");
        this.closeDeviceModal();
        await this.fetchDeviceList();
        this.mergeCreatedDevice(res);
      } catch (error) {
        const msg =
          (error && error.code === 40000 && (error.msg || error.message)) ||
          error.message ||
          "设备新增失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingDevice = false;
      }
    },
    startEditDevice(device) {
      this.editingDeviceId = device.deviceId;
      this.deviceEditDraft = device.deviceName;
    },
    cancelEditDevice() {
      this.editingDeviceId = null;
      this.deviceEditDraft = "";
    },
    async confirmEditDevice(device) {
      const name = (this.deviceEditDraft || "").trim();
      if (!name) {
        this.showMessage("warning", "设备名称不能为空");
        return;
      }

      this.savingDeviceEditId = device.deviceId;
      try {
        const res = await updateDevice({
          deviceId: device.deviceId,
          deviceName: name,
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "设备修改失败");
          return;
        }

        const target = this.devices.find((d) => d.deviceId === device.deviceId);
        if (target) {
          target.deviceName = name;
        }

        this.editingDeviceId = null;
        this.deviceEditDraft = "";
        this.showMessage("success", (res && res.msg) || "设备名称已修改");
        await this.fetchDeviceList();
      } catch (error) {
        const msg =
          (error && error.code === 40000 && (error.msg || error.message)) ||
          error.message ||
          "设备修改失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingDeviceEditId = null;
      }
    },
    deleteEditDevice(device) {
      this.$confirm(`确认删除设备「${device.deviceName}」？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await deleteDevice({
              deviceId: device.deviceId,
            });

            if (this.isBizError(res)) {
              this.showMessage("error", res.msg || "设备删除失败");
              return;
            }

            if (this.editingDeviceId === device.deviceId) {
              this.cancelEditDevice();
            }

            this.showMessage("success", (res && res.msg) || "设备已删除");
            await this.fetchDeviceList();
          } catch (error) {
            const msg =
              (error && error.code === 40000 && (error.msg || error.message)) ||
              error.message ||
              "设备删除失败，请稍后重试";
            this.showMessage("error", msg);
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.list-loading {
  padding: 40px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.empty-cell {
  padding: 24px 0;
  color: #999;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.device-list-section {
  margin-bottom: 0;
}

.device-list-section .form-label {
  font-size: 16px;
  font-weight: 600;
}

.add-btn {
  margin-top: 0;
  border: none;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 14px;
  font-size: 14px;
  cursor: pointer;
}

.add-btn:hover {
  background: #096dd9;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 12px;
  width: 420px;
  max-width: calc(100% - 32px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.modal-save-btn {
  width: 100%;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: #1890ff;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.modal-save-btn:hover {
  background: #096dd9;
}

.edit-device-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #eef1f6;
  font-size: 13px;
}

.edit-device-table th,
.edit-device-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 10px 12px;
  text-align: left;
  vertical-align: middle;
}

.edit-device-table th {
  background: #f8f9fc;
  font-weight: 600;
  color: #616f84;
}

.edit-device-table tbody tr:hover td {
  background: #f0f7ff;
}

.edit-device-action-col {
  width: 140px;
  white-space: nowrap;
}

.device-name-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-name-input {
  flex: 1;
  min-width: 0;
}

.mini-btn {
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
}

.mini-btn:last-child {
  margin-right: 0;
}

.mini-btn-edit {
  background: #fff;
  color: #409eff;
  border: 1px solid #409eff;
}

.mini-btn-confirm {
  background: #52c41a;
  color: #fff;
  border: 1px solid #52c41a;
}

.mini-btn-confirm:hover {
  background: #389e0d;
}

.mini-btn-confirm:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.mini-btn-cancel {
  background: #fff;
  color: #666;
  border: 1px solid #d9d9d9;
}

.mini-btn-delete {
  background: #ff4d4f;
  color: #fff;
}

.form-input:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}
</style>
